import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ItemSelectionStep from './ItemSelectionStep';
import StripeAddressAutocomplete from './StripeAddressAutocomplete';
import { resolveItemKey } from '../utils/itemUtils';
import {
  normalizeAddress,
  validateAddress,
  formatAddressLines,
  getValidationErrorMessage,
  emptyAddress,
  ADDRESS_LABELS,
} from '../utils/addressUtils';

const BASE_LOCKER_SLOTS = 8;
const FREE_SHIPPING_ITEM_THRESHOLD = 8;
const PAID_SHIPPING_FEE = 6.99;
const PAID_SHIPPING_FEE_LABEL = '$6.99';
const US_COUNTRY_LABEL = 'United States';
const US_COUNTRY_VALUES = new Set([
  'US',
  'USA',
  'UNITED STATES',
  'UNITED STATES OF AMERICA',
]);
const isUsCountry = (value = '') =>
  typeof value === 'string' && US_COUNTRY_VALUES.has(value.trim().toUpperCase());

const LockerShippingModal = ({
  isOpen,
  origin = 'manual',
  items = [],
  pendingPrize = null,
  maxLockerSlots = BASE_LOCKER_SLOTS,
  shippingAddress = null,
  onClose,
  onConfirm,
  loading = false,
  error = null,
}) => {
  const [step, setStep] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [addressDraft, setAddressDraft] = useState({
    ...emptyAddress,
    country: US_COUNTRY_LABEL,
  });
  const [addressSelection, setAddressSelection] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [isStripeComplete, setIsStripeComplete] = useState(false);
  const [addressError, setAddressError] = useState('');

  const itemsWithKeys = useMemo(
    () =>
      items.map((item, index) => {
        const key = resolveItemKey(item, index);
        return {key, item};
      }),
    [items],
  );

  const selectedCount = useMemo(() => selectedKeys.size, [selectedKeys]);
  const lockerCount = itemsWithKeys.length;
  const finalCapacity = maxLockerSlots > BASE_LOCKER_SLOTS ? BASE_LOCKER_SLOTS : maxLockerSlots;
  const pendingCount = pendingPrize ? 1 : 0;
  const remainingCount = lockerCount - selectedCount;
  const finalOccupancy = remainingCount + pendingCount;
  const shippingFree = selectedCount >= FREE_SHIPPING_ITEM_THRESHOLD;
  const shippingFee = shippingFree ? 0 : PAID_SHIPPING_FEE;
  const selectionValid = selectedCount > 0 && finalOccupancy <= finalCapacity;
  // Apply Stripe address to draft (like old project)
  const applyStripeAddress = useCallback((raw = {}) => {
    const normalized = normalizeAddress(raw);
    setAddressDraft((prev) => ({
      ...prev,
      ...normalized,
      country: isUsCountry(normalized.country) ? US_COUNTRY_LABEL : normalized.country,
    }));
    return normalized;
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedKeys(new Set());
      setAddressDraft({
        ...emptyAddress,
        country: US_COUNTRY_LABEL,
      });
      setAddressSelection(null);
      setShowManualForm(false);
      setIsStripeComplete(false);
      setAddressError('');
      return;
    }

    // Default to selecting all items
    const defaults = new Set(itemsWithKeys.map(({key}) => key));
    setSelectedKeys(defaults);

    // Initialize address from props if valid (like old project)
    const normalized = normalizeAddress(shippingAddress);
    const validExisting = validateAddress(normalized).isValid;
    let nextError = '';
    if (validExisting && isUsCountry(normalized.country)) {
      const normalizedUs = {
        ...normalized,
        country: US_COUNTRY_LABEL,
      };
      setAddressSelection(normalizedUs);
      setAddressDraft(normalizedUs);
    } else {
      setAddressSelection(null);
      setAddressDraft({
        ...emptyAddress,
        country: US_COUNTRY_LABEL,
      });
      if (validExisting && !isUsCountry(normalized.country)) {
        nextError = 'PonPon currently ships within the United States only.';
      }
    }
    setAddressError(nextError);
    setShowManualForm(false);
    setIsStripeComplete(false);
    setStep(1);
  }, [isOpen, itemsWithKeys, shippingAddress]);

  const handleToggleItem = (itemKey) => {
    if (loading) {
      return;
    }
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (loading) {
      return;
    }
    const all = new Set(itemsWithKeys.map(({key}) => key));
    setSelectedKeys(all);
  };

  const handleClearSelection = () => {
    if (loading) {
      return;
    }
    setSelectedKeys(new Set());
  };

  const handleNextFromStepOne = () => {
    if (!selectionValid) {
      return;
    }
    setStep(2);
  };

  // Handle Stripe address change (like old project)
  const handleStripeAddressChange = useCallback(
    (event) => {
      if (!event?.value) {
        setIsStripeComplete(false);
        return;
      }
      const {value, complete} = event;
      const normalized = applyStripeAddress({
        name: value.name,
        phone: value.phone,
        address: value.address,
      });
      if (!isUsCountry(normalized.country)) {
        setAddressError('PonPon currently ships within the United States only.');
        setIsStripeComplete(false);
        return;
      }
      const validation = validateAddress(normalized);
      const completeAndValid = Boolean(complete && validation.isValid);
      setIsStripeComplete(completeAndValid);
      if (completeAndValid) {
        setAddressError('');
      }
    },
    [applyStripeAddress],
  );

  // Handle manual form submission (like old project)
  const handleSubmitAddress = (event) => {
    event.preventDefault();
    const trimmed = normalizeAddress(addressDraft);
    if (!isUsCountry(trimmed.country)) {
      setAddressError('PonPon currently ships within the United States only.');
      return;
    }
    const validation = validateAddress(trimmed);
    if (!validation.isValid) {
      setAddressError(getValidationErrorMessage(validation));
      return;
    }
    const enforcedAddress = {
      ...trimmed,
      country: US_COUNTRY_LABEL,
    };
    setAddressSelection(enforcedAddress);
    setAddressDraft(enforcedAddress);
    setAddressError('');
    setShowManualForm(false);
    setIsStripeComplete(false);
    setStep(3);
  };

  // Handle Stripe autocomplete confirmation (like old project)
  const handleConfirmStripeAddress = () => {
    const normalized = normalizeAddress(addressDraft);
    if (!isUsCountry(normalized.country)) {
      setAddressError('PonPon currently ships within the United States only.');
      setIsStripeComplete(false);
      return;
    }
    const validation = validateAddress(normalized);
    if (!validation.isValid) {
      setAddressError(getValidationErrorMessage(validation));
      setIsStripeComplete(false);
      return;
    }
    const enforcedAddress = {
      ...normalized,
      country: US_COUNTRY_LABEL,
    };
    setAddressSelection(enforcedAddress);
    setAddressDraft(enforcedAddress);
    setAddressError('');
    setStep(3);
  };

  // Like old project: selectedAddressIsValid checks any valid addressSelection
  const selectedAddressIsValid = Boolean(
    addressSelection && validateAddress(addressSelection).isValid && !addressError,
  );

  const handleUseExistingAddress = () => {
    if (!addressSelection) {
      return;
    }
    setStep(3);
  };

  const handleConfirmShipment = () => {
    if (loading || !addressSelection) {
      return;
    }
    const itemIds = itemsWithKeys
        .filter(({key}) => selectedKeys.has(key))
        .map(({item}) => resolveItemKey(item, 0));
    onConfirm({
      itemIds,
      address: addressSelection,
      shippingFee,
    });
  };

  // Render footer actions based on step
  let footerAction = { secondary: null, primary: null };
  if (step === 1) {
    footerAction.primary = (
      <button
        type="button"
        onClick={handleNextFromStepOne}
        disabled={!selectionValid || loading}
        className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-5 py-2 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] disabled:translate-y-0 disabled:opacity-70"
      >
        <span>Next</span>
        <span aria-hidden="true">→</span>
      </button>
    );
  } else if (step === 2) {
    footerAction.secondary = (
      <button
        type="button"
        onClick={() => setStep(1)}
        disabled={loading}
        className="text-sm font-semibold text-ponpon-navy/60 underline-offset-2 hover:underline disabled:opacity-50"
      >
        Back
      </button>
    );
    // Like old project: priority order - selectedAddressIsValid > showManualForm > Stripe autocomplete
    if (selectedAddressIsValid) {
      footerAction.primary = (
        <button
          type="button"
          onClick={handleUseExistingAddress}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-5 py-2 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] disabled:translate-y-0 disabled:opacity-70"
        >
          <span>Next</span>
          <span aria-hidden="true">→</span>
        </button>
      );
    } else if (showManualForm) {
      footerAction.primary = (
        <button
          type="submit"
          form="locker-shipping-manual-form"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-5 py-2 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] disabled:translate-y-0 disabled:opacity-70"
        >
          <span>Next</span>
          <span aria-hidden="true">→</span>
        </button>
      );
    } else {
      // Stripe autocomplete: disabled when !isStripeComplete (like old project)
      footerAction.primary = (
        <button
          type="button"
          onClick={handleConfirmStripeAddress}
          disabled={!isStripeComplete || loading}
          className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-5 py-2 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] disabled:translate-y-0 disabled:opacity-70"
        >
          <span>Next</span>
          <span aria-hidden="true">→</span>
        </button>
      );
    }
  } else if (step === 3) {
    footerAction.secondary = (
      <button
        type="button"
        onClick={() => setStep(2)}
        disabled={loading}
        className="text-sm font-semibold text-ponpon-navy/60 underline-offset-2 hover:underline disabled:opacity-50"
      >
        Back
      </button>
    );
    footerAction.primary = (
      <button
        type="button"
        onClick={handleConfirmShipment}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-5 py-2 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] disabled:translate-y-0 disabled:opacity-70"
      >
        <span>{loading ? 'Submitting shipment...' : 'Confirm shipment'}</span>
        <span aria-hidden="true">🚀</span>
      </button>
    );
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-start justify-center overflow-y-auto bg-ponpon-navy/40 px-4 pt-[calc(var(--mobile-header-offset,3.25rem)+1.5rem)] pb-10 md:pb-16 md:pt-[calc(var(--app-header-offset,4rem)+1.5rem)] backdrop-blur-sm">
      <div className="relative w-full max-w-3xl rounded-3xl border border-white/70 bg-white/95 p-6 shadow-2xl md:max-h-[85vh] md:overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          disabled={loading && origin === 'forced'}
          className="absolute right-4 top-4 rounded-full border border-ponpon-navy/15 bg-white px-3 py-1 text-xs font-semibold text-ponpon-navy/70 transition hover:border-ponpon-coral/40 hover:text-ponpon-coral disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Close
        </button>
        <div className="flex flex-col gap-6">
          <header className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-ponpon-navy/45">
              Ship Your Locker - Step {step} of 3
            </span>
            <h2 className="text-2xl font-semibold text-ponpon-navy">
              {step === 1
                ? 'Select Items'
                : step === 2
                ? 'Shipping Address'
                : 'Review & Confirm'}
            </h2>
            {step === 1 ? (
              <p className="text-sm text-ponpon-navy/65">
                {pendingPrize
                  ? 'Your locker is full. Ship items now to free space for your pending prize.'
                  : 'Choose which items to send out!'}
              </p>
            ) : null}
            {step === 2 ? (
              <p className="text-sm text-ponpon-navy/65">
                PonPon currently ships within the United States only. International addresses aren&apos;t supported yet.
              </p>
            ) : null}
          </header>

          {step === 1 ? (
            <ItemSelectionStep
              items={items}
              selectedKeys={selectedKeys}
              pendingPrize={pendingPrize}
              maxLockerSlots={maxLockerSlots}
              loading={loading}
              onToggleItem={handleToggleItem}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
            />
          ) : null}

          {step === 2 ? (
            <section className="space-y-4">
              {addressSelection && validateAddress(addressSelection).isValid && !addressError ? (
                <div className="rounded-3xl border border-ponpon-navy/15 bg-white/90 p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-ponpon-navy">{addressSelection.name}</p>
                      <p className="text-xs text-ponpon-navy/60">{addressSelection.phone}</p>
                      <div className="mt-1 space-y-1 text-sm text-ponpon-navy/70">
                        {formatAddressLines(addressSelection).map((line) => (
                          <p key={line}>{line}</p>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setAddressDraft(addressSelection);
                        setAddressSelection(null);
                        setIsStripeComplete(false);
                        setShowManualForm(false);
                        setAddressError('');
                      }}
                      disabled={loading}
                      className="text-sm font-semibold text-ponpon-coral underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      Edit details
                    </button>
                  </div>
                </div>
              ) : !showManualForm ? (
                <StripeAddressAutocomplete
                  onChange={handleStripeAddressChange}
                  addressError={addressError}
                  onShowManualForm={() => {
                    setShowManualForm(true);
                    setIsStripeComplete(false);
                    setAddressError('');
                    setAddressDraft((prev) => ({
                      ...prev,
                      country: US_COUNTRY_LABEL,
                    }));
                  }}
                  loading={loading}
                />
              ) : (
                <form id="locker-shipping-manual-form" className="space-y-3" onSubmit={handleSubmitAddress}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col text-xs font-semibold text-ponpon-navy/60">
                      {ADDRESS_LABELS.name}
                      <input
                        type="text"
                        value={addressDraft.name}
                        onChange={(event) =>
                          setAddressDraft((prev) => ({...prev, name: event.target.value}))
                        }
                        className="mt-1 rounded-xl border border-ponpon-navy/15 bg-white px-3 py-2 text-sm text-ponpon-navy focus:border-ponpon-coral focus:outline-none focus:ring-2 focus:ring-ponpon-coral/20"
                        required
                        disabled={loading}
                      />
                    </label>
                    <label className="flex flex-col text-xs font-semibold text-ponpon-navy/60">
                      {ADDRESS_LABELS.phone}
                      <input
                        type="tel"
                        value={addressDraft.phone}
                        onChange={(event) =>
                          setAddressDraft((prev) => ({...prev, phone: event.target.value}))
                        }
                        className="mt-1 rounded-xl border border-ponpon-navy/15 bg-white px-3 py-2 text-sm text-ponpon-navy focus:border-ponpon-coral focus:outline-none focus:ring-2 focus:ring-ponpon-coral/20"
                        required
                        disabled={loading}
                      />
                    </label>
                  </div>
                  <label className="flex flex-col text-xs font-semibold text-ponpon-navy/60">
                    {ADDRESS_LABELS.addressLine1}
                    <input
                      type="text"
                      value={addressDraft.addressLine1}
                      onChange={(event) =>
                        setAddressDraft((prev) => ({...prev, addressLine1: event.target.value}))
                      }
                      className="mt-1 rounded-xl border border-ponpon-navy/15 bg-white px-3 py-2 text-sm text-ponpon-navy focus:border-ponpon-coral focus:outline-none focus:ring-2 focus:ring-ponpon-coral/20"
                      required
                      disabled={loading}
                    />
                  </label>
                  <label className="flex flex-col text-xs font-semibold text-ponpon-navy/60">
                    {ADDRESS_LABELS.addressLine2} (optional)
                    <input
                      type="text"
                      value={addressDraft.addressLine2}
                      onChange={(event) =>
                        setAddressDraft((prev) => ({...prev, addressLine2: event.target.value}))
                      }
                      className="mt-1 rounded-xl border border-ponpon-navy/15 bg-white px-3 py-2 text-sm text-ponpon-navy focus:border-ponpon-coral focus:outline-none focus:ring-2 focus:ring-ponpon-coral/20"
                      disabled={loading}
                    />
                  </label>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <label className="flex flex-col text-xs font-semibold text-ponpon-navy/60">
                      {ADDRESS_LABELS.city}
                      <input
                        type="text"
                        value={addressDraft.city}
                        onChange={(event) =>
                          setAddressDraft((prev) => ({...prev, city: event.target.value}))
                        }
                        className="mt-1 rounded-xl border border-ponpon-navy/15 bg-white px-3 py-2 text-sm text-ponpon-navy focus:border-ponpon-coral focus:outline-none focus:ring-2 focus:ring-ponpon-coral/20"
                        disabled={loading}
                      />
                    </label>
                    <label className="flex flex-col text-xs font-semibold text-ponpon-navy/60">
                      {ADDRESS_LABELS.state}
                      <input
                        type="text"
                        value={addressDraft.state}
                        onChange={(event) =>
                          setAddressDraft((prev) => ({...prev, state: event.target.value}))
                        }
                        className="mt-1 rounded-xl border border-ponpon-navy/15 bg-white px-3 py-2 text-sm text-ponpon-navy focus:border-ponpon-coral focus:outline-none focus:ring-2 focus:ring-ponpon-coral/20"
                        disabled={loading}
                      />
                    </label>
                    <label className="flex flex-col text-xs font-semibold text-ponpon-navy/60">
                      {ADDRESS_LABELS.postalCode}
                      <input
                        type="text"
                        value={addressDraft.postalCode}
                        onChange={(event) =>
                          setAddressDraft((prev) => ({...prev, postalCode: event.target.value}))
                        }
                        className="mt-1 rounded-xl border border-ponpon-navy/15 bg-white px-3 py-2 text-sm text-ponpon-navy focus:border-ponpon-coral focus:outline-none focus:ring-2 focus:ring-ponpon-coral/20"
                        required
                        disabled={loading}
                      />
                    </label>
                  </div>
                  <label className="flex flex-col text-xs font-semibold text-ponpon-navy/60">
                    {ADDRESS_LABELS.country}
                    <input
                      type="text"
                      value={US_COUNTRY_LABEL}
                      readOnly
                      className="mt-1 rounded-xl border border-ponpon-navy/15 bg-slate-50 px-3 py-2 text-sm text-ponpon-navy"
                    />
                  </label>
                  {addressError ? (
                    <p className="rounded-2xl bg-ponpon-coral/10 px-4 py-2 text-xs text-ponpon-coral/80">
                      {addressError}
                    </p>
                  ) : null}
                  <div className="flex items-center justify-between gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowManualForm(false);
                        setIsStripeComplete(false);
                        setAddressError('');
                      }}
                      disabled={loading}
                      className="text-sm font-semibold text-ponpon-navy/60 underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      Back to autocomplete
                    </button>
                  </div>
                </form>
              )}
            </section>
          ) : null}

          {step === 3 ? (
            <section className="space-y-5">
              <div className="rounded-3xl border border-ponpon-navy/10 bg-white/90 p-5 shadow-sm">
                <h3 className="text-base font-semibold text-ponpon-navy">Shipment summary</h3>
                <dl className="mt-3 grid gap-2 text-sm text-ponpon-navy/70">
                  <div className="flex items-center justify-between">
                    <dt>Number of items</dt>
                    <dd>{selectedCount}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt>Shipping fee</dt>
                    <dd>{shippingFee === 0 ? 'Free' : PAID_SHIPPING_FEE_LABEL}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt>Estimated delivery</dt>
                    <dd>3-5 days</dd>
                  </div>
                </dl>
              </div>
              <div className="rounded-3xl border border-ponpon-navy/10 bg-white/90 p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-base font-semibold text-ponpon-navy">Ship to</h3>
                  <button
                    type="button"
                    onClick={() => {
                      // Save current address to draft for pre-filling (like old project)
                      if (addressSelection) {
                        setAddressDraft(addressSelection);
                      }
                      setAddressSelection(null);
                      setShowManualForm(false);
                      setIsStripeComplete(false);
                      setAddressError('');
                      setStep(2);
                    }}
                    className="text-sm font-semibold text-ponpon-coral underline-offset-2 hover:underline"
                  >
                    Edit details
                  </button>
                </div>
                {addressSelection ? (
                  <div className="mt-3 space-y-1 text-sm text-ponpon-navy/70">
                    <p className="font-semibold text-ponpon-navy">{addressSelection.name}</p>
                    <p>{addressSelection.phone}</p>
                    {formatAddressLines(addressSelection).map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                ) : null}
              </div>
              {error ? (
                <p className="rounded-2xl bg-ponpon-coral/10 px-4 py-2 text-xs text-ponpon-coral/80">
                  {error}
                </p>
              ) : null}
            </section>
          ) : null}

          <footer className="mt-4 flex items-center justify-between border-t border-ponpon-navy/10 pt-4 text-xs text-ponpon-navy/55">
            <span>
              You have {lockerCount} {lockerCount === 1 ? 'item' : 'items'} ready. Locker holds {maxLockerSlots} max.
            </span>
          </footer>
          {(footerAction.primary || footerAction.secondary) ? (
            <div className="mt-6 flex items-center justify-between text-sm">
              <div>{footerAction.secondary}</div>
              <div>{footerAction.primary}</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LockerShippingModal;
